<template>
  <div
    v-if="item"
    class="bg-white hover:bg-gray-200 font-semibold relative flex items-center rounded-lg max-w-2xl overflow-hidden"
    style="-webkit-tap-highlight-color: transparent"
  >
    <input
      class="input hidden"
      v-model="completedLocal"
      :id="$vnode.data.key"
      :name="$vnode.data.key"
      type="checkbox"
    />
    <label
      class="label pl-16 pr-16 py-3 flex-grow text-left cursor-pointer rounded-lg leading-none relative"
      :class="completedLocal ? 'text-white' : ''"
      :for="$vnode.data.key"
    >
      <div
        class="flex items-center gap-2 text-xs uppercase tracking-wider mb-1"
        v-if="showMetadata"
      >
        <span
          class="font-bold text-gray-600"
          :class="completedLocal ? 'text-white' : ''"
        >
          {{ dueTime }}
        </span>
        <span class="text-gray-400" :class="{ 'text-gray-300': completedLocal }"
          >•</span
        >
        <span
          class="text-gray-500"
          :class="{ 'text-gray-300': completedLocal }"
        >
          {{ assignee }}
        </span>
      </div>
      <div class="text-base">{{ label }}</div>
    </label>
    <div
      class="min-w-[2rem] min-h-[2rem] w-8 h-8 mr-4 flex-shrink-0 flex items-center justify-center text-gray-400 cursor-pointer bg-transparent hover:text-gray-700"
      :class="{ 'text-white': completedLocal }"
      :title="descriptionIsUrl ? 'Open link' : 'More information'"
      v-if="item && item.description"
      @click.stop="showDescription"
    >
      <unicon
        :name="descriptionIsUrl ? 'external-link-alt' : 'info-circle'"
        fill="currentColor"
        :stroke="1"
        class="w-8 h-8"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
export default {
  name: 'TaskItem',
  props: {
    id: {
      type: String,
      required: true
    },
    log: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      completedLocal: false
    }
  },
  computed: {
    ...mapGetters('storeModule/tasks', ['taskById']),
    ...mapGetters('global', ['taskById']),
    completed() {
      try {
        return this.log[this.id] && this.log[this.id]['completed']
      } catch {
        return false
      }
    },
    item() {
      try {
        // Use global store for weekly/monthly tasks, storeModule for shift tasks
        const getter =
          this.log &&
          (this.log.type === 'weekly' || this.log.type === 'monthly')
            ? this.$store.getters['global/taskById']
            : this.$store.getters['storeModule/tasks/taskById']
        return getter(this.id)
      } catch {
        return null
      }
    },
    label() {
      try {
        return this.item.name
      } catch {
        return ''
      }
    },
    assignee() {
      try {
        return this.item.who || 'Unassigned'
      } catch {
        return 'Unassigned'
      }
    },
    dueTime() {
      try {
        if (!this.item.timeEnd) return 'No time'
        return this.item.timeEnd
        // return new Date(this.item.endTime).toLocaleTimeString('en-US', {
        //   hour: 'numeric',
        //   minute: '2-digit',
        //   hour12: true
        // })
      } catch {
        return 'No time'
      }
    },
    showMetadata() {
      // Only show metadata for daily tasks
      return (
        this.item &&
        this.item.frequency === 'daily' &&
        (this.dueTime !== 'No time' || this.assignee !== 'Unassigned')
      )
    },
    descriptionIsUrl() {
      try {
        const url = new URL(this.item && this.item.description)
        return url.protocol === 'http:' || url.protocol === 'https:'
      } catch {
        return false
      }
    }
  },
  methods: {
    showDescription() {
      if (this.item && this.item.description) {
        const desc = this.item.description
        try {
          const url = new URL(desc)
          if (url.protocol === 'http:' || url.protocol === 'https:') {
            window.open(desc, '_blank')
            return
          }
        } catch {}
        this.$modal.show('modal--message', { message: desc })
      }
    }
  },
  watch: {
    completed: {
      immediate: true,
      handler(newValue) {
        if (this.completedLocal !== newValue) this.completedLocal = newValue
      }
    },
    completedLocal(newValue) {
      this.$emit('putValue', newValue)
    }
  }
}
</script>

<style lang="scss" scoped>
// Text
//
.label {
  z-index: 2;
  transition: color 200ms ease-in;
}
.input:checked ~ .label {
  @apply text-white;
}
.input:checked:hover ~ .label {
  @apply text-white;
}

// Animated background
//
.label:before {
  @apply bg-gray-800 rounded-lg absolute opacity-0;
  width: 20px;
  height: 10px;
  content: '';
  left: 50%;
  top: 50%;
  will-change: transform;
  transform: translate(-50%, -50%) scale3d(1, 1, 1);
  transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1),
    opacity 300ms ease-in-out;
  z-index: -1;
}
.input:checked ~ .label:before {
  @apply opacity-100;
  transform: translate(-50%, -50%) scale3d(100, 100, 1);
}
.input:checked:hover ~ .label:before {
  @apply bg-gray-700;
}

// Remove any background from the icon when checked
.input:checked ~ .label + div {
  background-color: transparent;
}

//  Checkbox
//  |–– unchecked
.label:after {
  @apply rounded-full absolute w-8 h-8 border border-solid border-2 border-gray-800;
  content: '';
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  transition: transform 200ms ease-in;
}
//  |–– checked
.input:checked ~ .label:after {
  @apply bg-white border-white; //bg-teal-400 border-teal-400;
  background-image: url("data:image/svg+xml,<svg width='32' height='32' viewBox='0 0 32 32' xmlns='http://www.w3.org/2000/svg'><path d='M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z' fill='currentColor' /></svg>");
  background-repeat: no-repeat;
  background-position: 2px 3px;
}
//  |–– checked & hovered
.input:checked:hover ~ .label:after {
  @apply bg-gray-100 border-gray-100; //bg-teal-300 border-teal-300;
  background-image: url("data:image/svg+xml,<svg width='32' height='32' viewBox='0 0 32 32' xmlns='http://www.w3.org/2000/svg'><path d='M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z' fill='currentColor' /></svg>");
  background-repeat: no-repeat;
  background-position: 2px 3px;
}
</style>
